import React from "react";
import { ChevronRight, Lightbulb } from "lucide-react";
import type { Insight, InsightGroup, InsightsData } from "../types";
import {
    formatInsightGroupAsMarkdown,
    formatInsightPositionLabel,
    formatInsightsDate,
    groupInsightsByPosition,
} from "../utils/insightsUtils";
import CopyButton from "./CopyButton";
import CitationButton from "./CitationButton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";

const InsightItem: React.FC<{ insight: Insight; index: number }> = ({ insight, index }) => {
    const detailSections = [
        { heading: "Evidence", text: insight.evidence },
        { heading: "Drivers", text: insight.drivers },
    ].filter((section) => section.text);

    return (
        // --i is a running index across all groups so items cascade as one top-to-bottom reveal;
        // the lightbulb icon inherits it and glows as each item lands (see styles.css).
        <div className="chat-v2-insight-item" style={{ "--i": index } as React.CSSProperties}>
            <div className="chat-v2-insight-item-header">
                <span className="chat-v2-insight-icon">
                    <Lightbulb size={14} />
                </span>
                <span className="chat-v2-insight-item-title">{insight.title}</span>
                {insight.citations && insight.citations.length > 0 && (
                    <CitationButton citations={insight.citations} />
                )}
            </div>
            <p className="chat-v2-insight-observation">{insight.observation}</p>
            {detailSections.length > 0 && (
                <Collapsible defaultOpen className="chat-v2-insight-collapse">
                    <CollapsibleTrigger className="chat-v2-insight-collapse-trigger">
                        <ChevronRight size="1em" className="chat-v2-insight-collapse-caret" aria-hidden />
                        Details
                    </CollapsibleTrigger>
                    <CollapsibleContent className="chat-v2-insight-details">
                        {detailSections.map(({ heading, text }) => (
                            <p key={heading} className="chat-v2-insight-detail-text">{text}</p>
                        ))}
                    </CollapsibleContent>
                </Collapsible>
            )}
        </div>
    );
};

const InsightGroupCard: React.FC<{ group: InsightGroup; dateLabel: string; indexOffset: number }> = ({ group, dateLabel, indexOffset }) => {
    const positionLabel = formatInsightPositionLabel(group.positions);
    return (
        <div className="chat-v2-insight-group">
            <div className="chat-v2-insight-group-header">
                <span className="chat-v2-insight-position-tag">{positionLabel}</span>
                <CopyButton
                    text={formatInsightGroupAsMarkdown(group, dateLabel)}
                    ariaLabel={`Copy ${positionLabel} insights`}
                />
            </div>
            <div className="chat-v2-insight-group-items">
                {group.insights.map((insight, index) => (
                    <InsightItem key={index} insight={insight} index={indexOffset + index} />
                ))}
            </div>
        </div>
    );
};

const InsightsCard: React.FC<{ data: InsightsData | null | undefined; animate?: boolean }> = ({ data, animate }) => {
    const insights = data?.insights ?? [];
    const dateLabel = formatInsightsDate(data?.date);
    const groups = groupInsightsByPosition(insights);
    const hasInsights = groups.length > 0;

    // Running offset so each group's items continue the global cascade index rather than restarting.
    let itemOffset = 0;

    return (
        <div className={`chat-v2-insights-card${animate ? " chat-v2-artifact-animate" : ""}`}>
            <div className="chat-v2-insights-header">
                <span className="chat-v2-insights-title">AI Insights</span>
                {dateLabel && <span className="chat-v2-insights-date">{dateLabel}</span>}
            </div>
            {!hasInsights ? (
                <div className="chat-v2-insights-empty">
                    <Lightbulb size={20} />
                    <span className="chat-v2-insights-empty-title">No valid insights generated</span>
                    <span className="chat-v2-insights-empty-subtitle">
                        There wasn't enough notable activity{dateLabel ? ` on ${dateLabel}` : ""} to surface insights. Try a different date.
                    </span>
                </div>
            ) : (
                groups.map((group) => {
                    const indexOffset = itemOffset;
                    itemOffset += group.insights.length;
                    return <InsightGroupCard key={group.key} group={group} dateLabel={dateLabel} indexOffset={indexOffset} />;
                })
            )}
        </div>
    );
};

export default InsightsCard;
